import { useState, useEffect } from "react";
import { LedgerTransaction, WalletItem } from "../types";

export interface AnalyticsData {
  balanceOverTime: Array<{ date: string; balance: number }>;
  spendingByCategory: Array<{ name: string; value: number }>;
  transactionDistribution: Array<{ name: string; value: number }>;
  totalIssued: number;
  totalRedeemed: number;
  totalExpired: number;
  activeItems: number;
  redeemedItems: number;
  expiredItems: number;
}

/**
 * Hook to fetch and process analytics data
 * Transforms wallet items and transactions into analytics metrics
 */
export function useAnalyticsData(
  walletItems: WalletItem[] | null,
  transactions: LedgerTransaction[] | null
): { data: AnalyticsData | null; loading: boolean } {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!walletItems || !transactions) {
      setLoading(true);
      return;
    }

    try {
      // Calculate balance over time (rolling 30 days)
      const balanceOverTime = calculateBalanceOverTime(transactions);

      // Calculate spending by category (item type)
      const spendingByCategory = calculateSpendingByCategory(transactions, walletItems);

      // Calculate transaction distribution
      const transactionDistribution = calculateTransactionDistribution(
        transactions
      );

      // Calculate summary statistics
      const totalIssued = transactions
        .filter((tx) => tx.type === "ISSUE")
        .reduce((sum, tx) => sum + (tx.amount || 0), 0);

      const totalRedeemed = transactions
        .filter((tx) => tx.type === "REDEEM")
        .reduce((sum, tx) => sum + (tx.amount || 0), 0);

      const totalExpired = transactions
        .filter((tx) => tx.type === "EXPIRE")
        .reduce((sum, tx) => sum + (tx.amount || 0), 0);

      const activeItems = walletItems.filter(
        (item) => item.status === "ACTIVE"
      ).length;
      const redeemedItems = walletItems.filter(
        (item) => item.status === "REDEEMED"
      ).length;
      const expiredItems = walletItems.filter(
        (item) => item.status === "EXPIRED"
      ).length;

      setData({
        balanceOverTime,
        spendingByCategory,
        transactionDistribution,
        totalIssued,
        totalRedeemed,
        totalExpired,
        activeItems,
        redeemedItems,
        expiredItems,
      });
    } catch (error) {
      console.error("Error processing analytics data:", error);
    } finally {
      setLoading(false);
    }
  }, [walletItems, transactions]);

  return { data, loading };
}

/**
 * Calculate balance over time for the past 30 days
 */
function calculateBalanceOverTime(transactions: LedgerTransaction[]): Array<{
  date: string;
  balance: number;
}> {
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

  // Group transactions by day
  const byDay = new Map<string, LedgerTransaction[]>();

  transactions.forEach((tx) => {
    const date = tx.createdAt instanceof Date ? tx.createdAt : new Date(tx.createdAt);
    if (date >= thirtyDaysAgo) {
      const dateKey = date.toISOString().split("T")[0];
      if (!byDay.has(dateKey)) {
        byDay.set(dateKey, []);
      }
      byDay.get(dateKey)?.push(tx);
    }
  });

  // Calculate running balance
  let runningBalance = 0;
  const result: Array<{ date: string; balance: number }> = [];

  // Create array of all days in range
  const dateArray: string[] = [];
  const current = new Date(thirtyDaysAgo);
  while (current <= new Date()) {
    dateArray.push(current.toISOString().split("T")[0]);
    current.setDate(current.getDate() + 1);
  }

  dateArray.forEach((dateKey) => {
    const dayTransactions = byDay.get(dateKey) || [];
    dayTransactions.forEach((tx) => {
      if (tx.type === "ISSUE") {
        runningBalance += tx.amount || 0;
      } else if (tx.type === "REDEEM") {
        runningBalance -= tx.amount || 0;
      } else if (tx.type === "EXPIRE") {
        runningBalance -= tx.amount || 0;
      }
    });

    result.push({
      date: dateKey,
      balance: Math.max(0, runningBalance),
    });
  });

  return result;
}

/**
 * Calculate spending by item category (type)
 */
function calculateSpendingByCategory(
  transactions: LedgerTransaction[],
  walletItems: WalletItem[] | null
): Array<{ name: string; value: number }> {
  const categoryMap = new Map<string, number>();

  transactions
    .filter((tx) => tx.type === "REDEEM")
    .forEach((tx) => {
      // Find the wallet item to get its type
      const item = walletItems?.find((w) => w.walletItemId === tx.walletItemId);
      const category = item?.type || "Other";
      categoryMap.set(category, (categoryMap.get(category) || 0) + (tx.amount || 0));
    });

  return Array.from(categoryMap.entries()).map(([name, value]) => ({
    name: formatCategoryName(name),
    value,
  }));
}

/**
 * Calculate transaction type distribution
 */
function calculateTransactionDistribution(
  transactions: LedgerTransaction[]
): Array<{ name: string; value: number }> {
  const typeMap = new Map<string, number>();

  transactions.forEach((tx) => {
    const count = typeMap.get(tx.type) || 0;
    typeMap.set(tx.type, count + 1);
  });

  return Array.from(typeMap.entries())
    .map(([name, value]) => ({
      name: formatTransactionType(name),
      value,
    }))
    .sort((a, b) => b.value - a.value);
}

/**
 * Format category name for display
 */
function formatCategoryName(category: string): string {
  const names: Record<string, string> = {
    GIFTCARD: "Giftcards",
    RANGE_TOKEN: "Range Tokens",
    GREENFEE_TICKET: "Greenfee Tickets",
    Other: "Other",
  };
  return names[category] || category;
}

/**
 * Format transaction type for display
 */
function formatTransactionType(type: string): string {
  const types: Record<string, string> = {
    ISSUE: "Issued",
    CLAIM: "Claimed",
    REDEEM: "Redeemed",
    EXPIRE: "Expired",
    REFUND: "Refunded",
    REVERSE: "Reversed",
    BLOCK: "Blocked",
    UNBLOCK: "Unblocked",
  };
  return types[type] || type;
}
